import type { FunctionalUseQueryOptions } from 'app/types/network';
import { getBffQueryData, useQueryLPC } from 'app/lib/query';
import { http } from '../lib/axios';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai/index';
import { bffDiscoverScreenLoadedAtom } from 'app/atoms';
import { type Language, getI18nLanguage } from 'app/lib/language';
import type { Category, ServiceCategory, PopularItem } from 'app/types/category';
import { categoryKeys } from './keys';
import { useIsGuestWeb } from 'app/utils/web-helpers';
export type { Category } from 'app/types/category';
export const SERVICE_CATEGORY_ID = '144f546f-e689-495f-a559-cc54ac99a88b';
const PUBLIC_SERVICE_CATEGORY_ID = '33c5afa9-85ea-4976-aa33-cb16462b9929';

export const sortCategories = (categories?: Category[], language?: Language) => {
  if (!categories || !language) return categories;
  return categories.sort((c1, c2) => {
    // Other category should be at the end
    if (c1.name.fr === 'Autres' || c1.name.en === 'Other') return 1;
    if (c2.name.fr === 'Autres' || c2.name.en === 'Other') return -1;
    // Sort the rest alphabetically
    return c1.name[language].localeCompare(c2.name[language]);
  });
};

export function useAllCategories(opts: FunctionalUseQueryOptions<Category[]> = {}) {
  const client = useQueryClient();
  const bffDiscoverScreenLoaded = useAtomValue(bffDiscoverScreenLoadedAtom);
  const isGuestWeb = useIsGuestWeb();
  const i18nLanguage = getI18nLanguage();

  const queryKey = categoryKeys.list('all');
  return useQueryLPC<Category[]>({
    queryKey,
    queryFn: async () => {
      const categories =
        (bffDiscoverScreenLoaded ? getBffQueryData<Category[]>(client, queryKey) : undefined) ??
        (await http.get<Category[]>('/categories')).data;
      return sortCategories(categories, i18nLanguage)!;
    },
    ...opts,
    enabled: (opts.enabled ?? true) && (bffDiscoverScreenLoaded || isGuestWeb),
  });
}

export function useCanUseServices() {
  const { data: serviceCategories } = useAllServiceCategories();
  return Boolean(serviceCategories?.length);
}

export function useSimplifiedServices() {
  const { data: serviceCategories } = useAllServiceCategories();
  return serviceCategories?.length === 1 ? serviceCategories[0]!.id : false;
}

export function isPublicServiceCategory(categoryId?: ServiceCategory['id'] | null) {
  return categoryId === PUBLIC_SERVICE_CATEGORY_ID;
}

export function useAllServiceCategories(opts: FunctionalUseQueryOptions<ServiceCategory[]> = {}) {
  const client = useQueryClient();
  const bffDiscoverScreenLoaded = useAtomValue(bffDiscoverScreenLoadedAtom);

  const queryKey = categoryKeys.list('allServices');
  return useQueryLPC<ServiceCategory[]>({
    queryKey,
    queryFn: async () =>
      (bffDiscoverScreenLoaded ? getBffQueryData<ServiceCategory[]>(client, queryKey) : undefined) ??
      (await http.get<ServiceCategory[]>('/categories/services')).data,
    ...opts,
    enabled: (opts.enabled ?? true) && bffDiscoverScreenLoaded,
  });
}

export function usePopularItemsByCategory(opts: FunctionalUseQueryOptions<Category[]> = {}) {
  const client = useQueryClient();
  const bffDiscoverScreenLoaded = useAtomValue(bffDiscoverScreenLoadedAtom);

  const queryKey = categoryKeys.list('popularItemsByCat');
  return useQueryLPC<Category[]>({
    queryKey,
    queryFn: async () =>
      (bffDiscoverScreenLoaded ? getBffQueryData<Category[]>(client, queryKey) : undefined) ??
      (await http.get<Category[]>('/categories/popular_items')).data,
    ...opts,
    enabled: (opts.enabled ?? true) && bffDiscoverScreenLoaded,
  });
}

export interface PopularItemsByTag {
  tagId: string;
  popularItems: PopularItem[];
}

export function usePopularItemsByTag(opts: FunctionalUseQueryOptions<PopularItemsByTag[]> = {}) {
  const client = useQueryClient();
  const bffDiscoverScreenLoaded = useAtomValue(bffDiscoverScreenLoadedAtom);

  const queryKey = categoryKeys.list('popularItemsByTag');
  return useQueryLPC<PopularItemsByTag[]>({
    queryKey,
    queryFn: async () =>
      (bffDiscoverScreenLoaded ? getBffQueryData<PopularItemsByTag[]>(client, queryKey) : undefined) ??
      (await http.get<PopularItemsByTag[]>('/categories/tags/popular_items')).data,
    ...opts,
    enabled: (opts.enabled ?? true) && bffDiscoverScreenLoaded,
  });
}
